import { useCallback, useEffect, useState } from "react";
import {
  Box, Stack, Typography, TextField, Button, Tabs, Tab, Paper, Alert, AlertTitle,
  CircularProgress, ToggleButtonGroup, ToggleButton, Chip, Collapse, Tooltip, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloudSyncOutlinedIcon from "@mui/icons-material/CloudSyncOutlined";
import {
  Emitter, listEmitters, getInformeProvincias, downloadInformeProvinciasCsv, refreshPadron,
  type InformeProvincias, type InformeCriterio, type PadronRefreshResult,
} from "../services/billing";
import { BRAND } from "../styles/theme";

/**
 * CP-250 — Informe de facturación por provincia, para liquidar ingresos brutos.
 * Un informe por sociedad emisora (solapas, como en Facturación): nunca se
 * mezclan. Por mes calendario: neto / IVA / total por provincia del domicilio
 * del cliente, % sobre el mes, y lo no identificable en "Sin provincia" con el
 * motivo, para que el operador lo corrija.
 *
 * CP-374: la provincia sale del domicilio fiscal que devuelve el padrón de ARCA
 * (persistido por CUIT con fecha); onboarding / texto del domicilio quedan como
 * respaldo. El panel "Fuente de la provincia" y el bloque del padrón muestran
 * la trazabilidad; "Consultar padrón ARCA" resuelve lo que quedó sin provincia.
 */

const nf = new Intl.NumberFormat("es-AR");
const money = (n: number) =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const pct = (n: number) => `${(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`;

/** Mes anterior en YYYY-MM: IIBB se liquida sobre el mes cerrado. */
function defaultMonth(): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function fmtFecha(yyyymmdd: string): string {
  return yyyymmdd.length === 8 ? `${yyyymmdd.slice(6, 8)}/${yyyymmdd.slice(4, 6)}/${yyyymmdd.slice(0, 4)}` : yyyymmdd || "—";
}

const FUENTE_LABEL: Record<string, string> = {
  "arca":            "padrón ARCA (domicilio fiscal)",
  "arca-cp":         "padrón ARCA (código postal)",
  "receptor":        "provincia del receptor",
  "onboarding":      "onboarding del colector",
  "domicilio-texto": "texto del domicilio",
  "domicilio-cp":    "código postal del domicilio",
  "ninguna":         "sin provincia",
};
const FUENTE_TITLE: Record<string, string> = {
  "arca":            "idProvincia / descripcionProvincia del domicilio fiscal en la constancia de inscripción de ARCA. Fuente primaria.",
  "arca-cp":         "ARCA no informó la provincia del domicilio fiscal; se derivó del código postal que devolvió (CPA por letra o rango).",
  "receptor":        "Provincia cargada a mano en el receptor. Respaldo: sólo cuando ARCA no devolvió nada para el CUIT.",
  "onboarding":      "data_address_state del onboarding del colector (declarado por un tercero, sin validar). Respaldo.",
  "domicilio-texto": "Nombre de provincia en el texto libre del domicilio del receptor. Respaldo.",
  "domicilio-cp":    "Código postal en el texto libre del domicilio del receptor (heurístico). Respaldo.",
  "ninguna":         "Ninguna fuente dio una provincia: ver el detalle de abajo.",
};

function fmtFechaIso(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso.slice(0, 10) : d.toLocaleDateString("es-AR");
}

function StatTile({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <Box sx={{ minWidth: 170, p: 1.5, borderRadius: 2, border: "1px solid rgba(0,0,0,0.08)" }}>
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.15, color: color ?? "#222" }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Box>
  );
}

export default function BillingProvincias() {
  const [month, setMonth] = useState<string>(defaultMonth());
  const [criterio, setCriterio] = useState<InformeCriterio>("comprobante");
  const [emitters, setEmitters] = useState<Emitter[]>([]);
  const [emitterCuit, setEmitterCuit] = useState<number | null>(null);
  const [data, setData] = useState<InformeProvincias | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState<"resumen" | "detalle" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showSinProv, setShowSinProv] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState<PadronRefreshResult | null>(null);

  useEffect(() => {
    listEmitters()
      .then((r) => { setEmitters(r.emitters); setEmitterCuit(r.default_cuit); })
      .catch((e) => setErr(`No se pudo cargar el listado de emisores: ${e.message ?? e}`));
  }, []);

  const load = useCallback(async () => {
    if (!emitterCuit || !/^\d{4}-\d{2}$/.test(month)) return;
    setLoading(true); setErr(null);
    // Con detalle: el botón "Consultar padrón ARCA" necesita saber qué CUITs del
    // mes NO salieron de ARCA (no sólo los que quedaron sin provincia).
    try { setData(await getInformeProvincias(month, emitterCuit, criterio, true)); }
    catch (e: any) { setErr(String(e?.message ?? e)); setData(null); }
    finally { setLoading(false); }
  }, [month, emitterCuit, criterio]);
  useEffect(() => { void load(); }, [load]);

  async function onDownload(detalle: boolean) {
    if (!emitterCuit) return;
    setDownloading(detalle ? "detalle" : "resumen"); setErr(null);
    try {
      const { url, filename } = await downloadInformeProvinciasCsv(month, emitterCuit, criterio, detalle);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e: any) {
      setErr(String(e?.message ?? e));
    } finally { setDownloading(null); }
  }

  /** Consulta el padrón de ARCA para esos CUITs (máximo 25 por llamada, lo que
   *  entra en el timeout de API Gateway) y regenera el informe. */
  async function onRefreshPadron(cuits: string[]) {
    const unicos = Array.from(new Set(cuits)).slice(0, 25);
    if (!unicos.length) return;
    setRefreshing(true); setErr(null); setRefreshResult(null);
    try {
      const r = await refreshPadron(unicos);
      setRefreshResult(r);
      await load();
    } catch (e: any) {
      setErr(String(e?.message ?? e));
    } finally { setRefreshing(false); }
  }

  const emitter = emitters.find((e) => e.cuit === emitterCuit);
  const sinProv = data?.provincias.find((p) => p.provincia === "Sin provincia");
  const tot = data?.totales;
  const cuitsSinProv = Array.from(new Set((data?.sin_provincia ?? []).map((d) => d.cuit).filter(Boolean)));
  const padron = data?.padron;
  const fuenteArca = (data?.fuentes["arca"] ?? 0) + (data?.fuentes["arca-cp"] ?? 0);

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>Facturación por provincia</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Base para la liquidación de Ingresos Brutos: monto facturado por mes calendario según la provincia del
        domicilio fiscal del cliente según el padrón de ARCA, con el IVA discriminado. Cada sociedad tiene su informe por separado.
      </Typography>

      {emitters.length > 0 && (
        <Box sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Sociedad</Typography>
          <Tabs
            value={emitterCuit ?? false}
            onChange={(_, v) => setEmitterCuit(v)}
            textColor="primary" indicatorColor="primary"
            sx={{ "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: 15, minHeight: 48 } }}
          >
            {emitters.map((em) => (
              <Tab key={em.cuit} value={em.cuit} label={
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <span>{em.razon_social}</span>
                  <Typography variant="caption" color="text.secondary">CUIT {em.cuit}</Typography>
                </Stack>
              } />
            ))}
          </Tabs>
        </Box>
      )}

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} mb={3} alignItems={{ xs: "stretch", md: "center" }} flexWrap="wrap" useFlexGap>
        <TextField
          label="Mes" type="month" size="small" value={month}
          onChange={(e) => setMonth(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ minWidth: 170 }}
        />
        <Tooltip title={
          "Fecha de comprobante: el mes lo define la fecha del comprobante en AFIP (lo que rige fiscalmente). " +
          "Período facturado: el mes del servicio facturado, aunque la factura se haya emitido después."
        }>
          <ToggleButtonGroup
            size="small" exclusive value={criterio}
            onChange={(_, v) => v && setCriterio(v)}
          >
            <ToggleButton value="comprobante" sx={{ textTransform: "none" }}>Fecha de comprobante</ToggleButton>
            <ToggleButton value="periodo" sx={{ textTransform: "none" }}>Período facturado</ToggleButton>
          </ToggleButtonGroup>
        </Tooltip>
        <Tooltip title="Refrescar"><span>
          <IconButton size="small" onClick={load} disabled={loading}><RefreshIcon fontSize="small" /></IconButton>
        </span></Tooltip>
        {loading && <CircularProgress size={18} />}
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained" startIcon={downloading === "resumen" ? <CircularProgress size={14} color="inherit" /> : <FileDownloadOutlinedIcon />}
          onClick={() => onDownload(false)} disabled={!data || !!downloading}
        >
          Descargar CSV
        </Button>
        <Tooltip title="Una fila por factura, con CUIT, razón social, provincia y de dónde salió (para auditar el resumen).">
          <span>
            <Button
              variant="outlined" startIcon={downloading === "detalle" ? <CircularProgress size={14} /> : <FileDownloadOutlinedIcon />}
              onClick={() => onDownload(true)} disabled={!data || !!downloading}
            >
              Detalle por factura
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {data && tot && (
        <>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <StatTile label="Neto (base imponible)" value={money(tot.neto)} sub={`${emitter?.razon_social ?? ""} · ${month}`} color="#166534" />
            <StatTile label="IVA" value={money(tot.iva)} sub="discriminado (21 %)" />
            <StatTile label="Total facturado" value={money(tot.total)} sub="neto + IVA" />
            <StatTile label="Facturas" value={nf.format(tot.facturas)} sub={`${nf.format(tot.clientes)} cliente(s)`} />
            <StatTile
              label="Sin provincia" value={money(sinProv?.neto ?? 0)}
              sub={sinProv ? `${nf.format(sinProv.filas)} fila(s) · ${pct(sinProv.pct_neto)} del neto` : "todo identificado"}
              color={sinProv ? "#B45309" : "#166534"}
            />
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }} alignItems="center">
            <Typography variant="caption" color="text.secondary">Fuente de la provincia:</Typography>
            {Object.entries(data.fuentes)
              .sort(([a], [b]) => (Object.keys(FUENTE_LABEL).indexOf(a) - Object.keys(FUENTE_LABEL).indexOf(b)))
              .map(([k, n]) => (
                <Tooltip key={k} title={FUENTE_TITLE[k] ?? ""}>
                  <Chip size="small" variant={k.startsWith("arca") ? "filled" : "outlined"}
                        label={`${FUENTE_LABEL[k] ?? k}: ${nf.format(n)}`}
                        color={k === "ninguna" ? "warning" : k.startsWith("arca") ? "success" : "default"} />
                </Tooltip>
              ))}
            {data.notas_credito > 0 && (
              <Chip size="small" color="info" label={`${nf.format(data.notas_credito)} nota(s) de crédito (restan)`} />
            )}
          </Stack>

          {padron && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }} alignItems="center">
              <Typography variant="caption" color="text.secondary">Padrón ARCA ({nf.format(padron.clientes)} clientes del mes):</Typography>
              <Chip size="small" variant="outlined" color={padron.con_provincia === padron.clientes ? "success" : "default"}
                    label={`con domicilio fiscal: ${nf.format(padron.con_provincia)}`} />
              {padron.sin_consultar > 0 && <Chip size="small" variant="outlined" color="warning" label={`sin consultar: ${nf.format(padron.sin_consultar)}`} />}
              {padron.con_error > 0 && <Chip size="small" variant="outlined" color="error" label={`con error: ${nf.format(padron.con_error)}`} />}
              {padron.sin_provincia > 0 && <Chip size="small" variant="outlined" color="warning" label={`sin provincia en ARCA: ${nf.format(padron.sin_provincia)}`} />}
              {padron.fecha_min && (
                <Tooltip title={`El job semanal reconsulta los CUITs con dato de más de ${padron.vigencia_dias} días.`}>
                  <Typography variant="caption" color="text.secondary">
                    datos consultados {fmtFechaIso(padron.fecha_min) === fmtFechaIso(padron.fecha_max)
                      ? `el ${fmtFechaIso(padron.fecha_min)}`
                      : `entre el ${fmtFechaIso(padron.fecha_min)} y el ${fmtFechaIso(padron.fecha_max)}`}
                  </Typography>
                </Tooltip>
              )}
              {fuenteArca < tot.filas && (
                <Tooltip title="Consulta el padrón de ARCA para los clientes del mes cuya provincia no salió de ARCA (hasta 25 por vez) y regenera el informe.">
                  <span>
                    <Button size="small" variant="outlined" disabled={refreshing || loading}
                            startIcon={refreshing ? <CircularProgress size={14} /> : <CloudSyncOutlinedIcon />}
                            onClick={() => onRefreshPadron(
                              (data.detalle ?? data.sin_provincia).filter((d) => !d.fuente.startsWith("arca")).map((d) => d.cuit))}>
                      Consultar padrón ARCA
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Stack>
          )}

          {refreshResult && (
            <Alert severity={refreshResult.errores.length ? "warning" : "success"} sx={{ mb: 2 }} onClose={() => setRefreshResult(null)}>
              Padrón ARCA: {nf.format(refreshResult.consultados)} CUIT(s) consultado(s)
              {refreshResult.ok.filter((o) => o.provincia_anterior).length > 0 && (
                <> · cambios de provincia: {refreshResult.ok.filter((o) => o.provincia_anterior)
                  .map((o) => `${o.razon_social || o.cuit}: ${o.provincia_anterior} → ${o.provincia}`).join("; ")}</>
              )}
              {refreshResult.errores.length > 0 && (
                <> · con error: {refreshResult.errores.map((e) => `${e.cuit} (${e.error})`).join("; ")}</>
              )}
              {refreshResult.pendientes.length > 0 && <> · quedaron {refreshResult.pendientes.length} sin consultar por tiempo: volvé a apretar.</>}
            </Alert>
          )}

          <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: "auto", mb: 3 }}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead sx={{ bgcolor: BRAND.primaryLight }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Provincia</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Neto</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">IVA</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">% s/neto</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">% s/total</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Facturas</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Clientes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.provincias.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3, color: "text.secondary" }}>
                    Sin facturación de {emitter?.razon_social ?? "esta sociedad"} en {month}.
                  </TableCell></TableRow>
                ) : data.provincias.map((p) => {
                  const sin = p.provincia === "Sin provincia";
                  return (
                    <TableRow key={p.provincia} hover sx={sin ? { bgcolor: "#FFF7ED" } : undefined}>
                      <TableCell sx={{ fontWeight: sin ? 700 : 500, color: sin ? "#B45309" : undefined }}>
                        {sin && <WarningAmberIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "text-bottom" }} />}
                        {p.provincia}
                        {p.provincia_id != null && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>AFIP {p.provincia_id}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{money(p.neto)}</TableCell>
                      <TableCell align="right">{money(p.iva)}</TableCell>
                      <TableCell align="right">{money(p.total)}</TableCell>
                      <TableCell align="right">{pct(p.pct_neto)}</TableCell>
                      <TableCell align="right">{pct(p.pct_total)}</TableCell>
                      <TableCell align="right">{nf.format(p.facturas)}</TableCell>
                      <TableCell align="right">{nf.format(p.clientes)}</TableCell>
                    </TableRow>
                  );
                })}
                {data.provincias.length > 0 && (
                  <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                    <TableCell sx={{ fontWeight: 700 }}>TOTAL {emitter?.razon_social ?? ""}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{money(tot.neto)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{money(tot.iva)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{money(tot.total)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{pct(tot.neto ? 100 : 0)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{pct(tot.total ? 100 : 0)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{nf.format(tot.facturas)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{nf.format(tot.clientes)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {data.sin_provincia.length > 0 && (
            <Alert severity="warning" icon={false} sx={{ borderRadius: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AlertTitle sx={{ m: 0 }}>
                  {nf.format(data.sin_provincia.length)} factura(s) sin provincia identificable — {money(sinProv?.neto ?? 0)} de neto
                </AlertTitle>
                <IconButton size="small" onClick={() => setShowSinProv((v) => !v)}>
                  {showSinProv ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Stack>
              <Typography variant="body2" sx={{ mb: 1 }}>
                El monto no se pierde (está en la fila "Sin provincia"), pero para la liquidación hay que asignarlo.
                Primero <b>Consultar padrón ARCA</b> (trae el domicilio fiscal oficial); si ARCA no tiene el CUIT o no
                informa domicilio, cargá la <b>Provincia</b> del receptor desde Facturación (lápiz del cliente).
              </Typography>
              <Button size="small" variant="contained" color="warning" sx={{ mb: 1 }} disabled={refreshing || loading || !cuitsSinProv.length}
                      startIcon={refreshing ? <CircularProgress size={14} color="inherit" /> : <CloudSyncOutlinedIcon />}
                      onClick={() => onRefreshPadron(cuitsSinProv)}>
                Consultar padrón ARCA para {nf.format(cuitsSinProv.length)} CUIT(s)
              </Button>
              <Collapse in={showSinProv}>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Comprobante</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>CUIT</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Razón social</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Collector</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Neto</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Padrón ARCA</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Motivo</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.sin_provincia.map((d) => (
                        <TableRow key={`${d.cae}|${d.collector}`} hover>
                          <TableCell sx={{ fontFamily: "monospace" }}>{d.comprobante}</TableCell>
                          <TableCell>{fmtFecha(d.fecha)}</TableCell>
                          <TableCell sx={{ fontFamily: "monospace" }}>{d.cuit}</TableCell>
                          <TableCell>{d.razon_social || "—"}</TableCell>
                          <TableCell>{d.collector}</TableCell>
                          <TableCell align="right">{money(d.neto)}</TableCell>
                          <TableCell sx={{ color: "text.secondary" }}>
                            {d.arca_fecha ? `consultado ${fmtFechaIso(d.arca_fecha)}${d.arca_cp ? ` · CP ${d.arca_cp}` : " · sin CP"}` : (d.arca_estado || "—")}
                          </TableCell>
                          <TableCell sx={{ color: "text.secondary" }}>{d.motivo.split("; ").slice(-1)[0]}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </Alert>
          )}
        </>
      )}
    </Box>
  );
}
